import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { ThemeContext } from 'styled-components';
import { campaignStatusEnum, campaignStatusColorEnum } from 'modules/campaigns/enums';
import campaignsStrings from 'modules/campaigns/strings';
import Popup from 'modules/common/components/Popup';
import Loader from 'modules/common/components/Loader';
import SectionDivider from 'modules/common/components/SectionDivider';

import {
  Wrapper,
  GridWrapper,
  Header,
  Divider,
  ItemColumn,
  ItemRow,
  ItemWrapper,
  ItemText,
  ItemSubText,
  ItemBtn,
  ItemStatus,
  ItemTag,
  ActionsPopupWrapper,
  ActionsPopupTitleWrapper,
  ActionsPopupTitle,
  ActionsPopupBtnWrapper,
  ActionsPopupYesBtn,
  ActionsPopupNoBtn,
} from './CampaignsList.style';

const CampaignActionsPopup = ({
  themeContext,
  isActionPopupLoading,
  actionStatus,
  handleActionPopupConfirmBtnClick,
  handleActionPopupDismissBtnClick,
}) => (
  <Popup handleCloseBtnClick={() => handleActionPopupDismissBtnClick()}>
    <ActionsPopupWrapper>
      <ActionsPopupTitleWrapper>
        <ActionsPopupTitle>
          {campaignsStrings.campaignsList.actionPopupTitle(
            campaignStatusEnum.getKeyByValue(actionStatus),
          )}
        </ActionsPopupTitle>
      </ActionsPopupTitleWrapper>
      <SectionDivider
        gap={16}
        withSeparator
        separatorGap={16}
        borderColor={themeContext.colors.grey004}
      />
      <ActionsPopupBtnWrapper>
        {(isActionPopupLoading && <Loader width={28} height={28} thickness={4} />) || (
          <>
            <ActionsPopupYesBtn as="button" onClick={() => handleActionPopupConfirmBtnClick()}>
              {campaignsStrings.campaignsList.actionPopupYesBtn}
            </ActionsPopupYesBtn>
            <ActionsPopupNoBtn as="button" onClick={() => handleActionPopupDismissBtnClick()}>
              {campaignsStrings.campaignsList.actionPopupNoBtn}
            </ActionsPopupNoBtn>
          </>
        )}
      </ActionsPopupBtnWrapper>
    </ActionsPopupWrapper>
  </Popup>
);
CampaignActionsPopup.propTypes = {
  themeContext: PropTypes.shape({
    colors: PropTypes.shape({
      grey004: PropTypes.string,
    }),
  }).isRequired,
  isActionPopupLoading: PropTypes.bool.isRequired,
  actionStatus: PropTypes.number.isRequired,
  handleActionPopupConfirmBtnClick: PropTypes.func.isRequired,
  handleActionPopupDismissBtnClick: PropTypes.func.isRequired,
};

const CampaignsList = ({
  campaignsListArr,
  isActionPopupOpen,
  isActionPopupLoading,
  actionPopupStatus,
  formatCampaignDate,
  handleEditCampaignBtnClick,
  handleAddAdvertBtnClick,
  handleViewAdvertsBtnClick,
  handleActionBtnClick,
  handleActionPopupConfirmBtnClick,
  handleActionPopupDismissBtnClick,
}) => {
  const themeContext = useContext(ThemeContext);
  const { colors } = themeContext;

  const campaignsGridHeaderList = campaignsStrings.campaignsList.campaignsGridHeader;

  return (
    <>
      <Wrapper>
        <GridWrapper>
          <Divider />
          {Object.keys(campaignsGridHeaderList).map(campaignsGridHeaderKey => (
            <Header key={campaignsGridHeaderKey}>
              {campaignsGridHeaderList[campaignsGridHeaderKey]}
            </Header>
          ))}
          <Divider />
          {campaignsListArr.map((campaign, index) => {
            const { campaignStatusId } = campaign.campaignStatusDto;
            const campaignStatusStr = campaignStatusEnum.getKeyByValue(campaignStatusId);
            const { color = colors.white, backgroundColor = colors.greenMain } =
              campaignStatusColorEnum(colors)[campaignStatusId] || {};

            const startDateStr = formatCampaignDate(campaign.startDate);
            const endDateStr = formatCampaignDate(campaign.endDate);

            const isLastItem = index === campaignsListArr.length - 1;

            return (
              <React.Fragment key={campaign.campaignKey}>
                <ItemColumn>
                  <ItemText>{campaign.campaignName}</ItemText>
                </ItemColumn>
                <ItemColumn>
                  <ItemStatus color={color} backgroundColor={backgroundColor}>
                    {campaignStatusStr}
                  </ItemStatus>
                </ItemColumn>
                <ItemColumn>
                  <ItemText>{campaign.selectedCampaignType.name}</ItemText>
                </ItemColumn>
                <ItemColumn>
                  <ItemText>{campaign.selectedCampaignBilling.name}</ItemText>
                </ItemColumn>
                <ItemColumn>
                  {campaign.zones.map(zone => (
                    <ItemTag key={zone.zoneKey}>{zone.zoneName}</ItemTag>
                  ))}
                </ItemColumn>
                <ItemColumn>
                  <ItemText>{startDateStr}</ItemText>
                </ItemColumn>
                <ItemColumn>
                  <ItemText>{endDateStr}</ItemText>
                </ItemColumn>
                <ItemColumn>
                  {campaign.campaignTargetings.map(target => (
                    <ItemWrapper key={target.key}>
                      <ItemText>{target.targetKeyName}</ItemText>
                      <SectionDivider gap={2} />
                      <ItemSubText>{target.targetKeyValue}</ItemSubText>
                    </ItemWrapper>
                  ))}
                </ItemColumn>
                <ItemRow>
                  <ItemBtn
                    as="button"
                    icon={'"\\e905"'}
                    onClick={() => handleViewAdvertsBtnClick(campaign.campaignKey)}
                  />
                  <ItemBtn
                    as="button"
                    icon={'"\\e910"'}
                    onClick={() => handleAddAdvertBtnClick(campaign.campaignKey)}
                  />
                  <ItemBtn
                    as="button"
                    icon={'"\\e90a"'}
                    onClick={() => handleEditCampaignBtnClick(campaign)}
                  />
                  {campaignStatusId === campaignStatusEnum.Inactive && (
                    <ItemBtn
                      as="button"
                      icon={'"\\e908"'}
                      onClick={() =>
                        handleActionBtnClick(campaign.campaignKey, campaignStatusEnum.Running)
                      }
                    />
                  )}
                  {campaignStatusId === campaignStatusEnum.Running && (
                    <ItemBtn
                      as="button"
                      backgroundColor={colors.grey005}
                      icon={'"\\e91f"'}
                      onClick={() =>
                        handleActionBtnClick(campaign.campaignKey, campaignStatusEnum.Inactive)
                      }
                    />
                  )}
                </ItemRow>
                {isLastItem === false && <Divider />}
              </React.Fragment>
            );
          })}
        </GridWrapper>
      </Wrapper>
      {isActionPopupOpen && (
        <CampaignActionsPopup
          themeContext={themeContext}
          isActionPopupLoading={isActionPopupLoading}
          actionStatus={actionPopupStatus}
          handleActionPopupConfirmBtnClick={handleActionPopupConfirmBtnClick}
          handleActionPopupDismissBtnClick={handleActionPopupDismissBtnClick}
        />
      )}
    </>
  );
  // ###############################################################
  // <----------------------- End Return UI ----------------------->
  // ###############################################################
};

CampaignsList.propTypes = {
  campaignsListArr: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  isActionPopupOpen: PropTypes.bool.isRequired,
  isActionPopupLoading: PropTypes.bool.isRequired,
  actionPopupStatus: PropTypes.number.isRequired,
  formatCampaignDate: PropTypes.func.isRequired,
  handleEditCampaignBtnClick: PropTypes.func.isRequired,
  handleAddAdvertBtnClick: PropTypes.func.isRequired,
  handleViewAdvertsBtnClick: PropTypes.func.isRequired,
  handleActionBtnClick: PropTypes.func.isRequired,
  handleActionPopupConfirmBtnClick: PropTypes.func.isRequired,
  handleActionPopupDismissBtnClick: PropTypes.func.isRequired,
};

CampaignsList.defaultProps = {};

export default CampaignsList;
